// Toast — vanilla rebuild of Sonner's design language and core behaviour.
//
// Sonner (https://sonner.emilkowal.ski) is React-only; this module ports the
// visual recipe and the API shape (toast, toast.success/error/warning/info,
// toast.loading, toast.promise, toast.dismiss) into a framework-agnostic
// vanilla bundle. It does not replicate the collapsed-stack-on-screen-edge
// trick — that adds a lot of complexity for a marginal aesthetic win in our
// kind of app.
//
// Public API:
//
//   BF.toast.show({ title, description, type, duration, action, cancel })
//   BF.toast.success(title, options?)
//   BF.toast.error(title, options?)
//   BF.toast.warning(title, options?)
//   BF.toast.info(title, options?)
//   BF.toast.loading(title, options?)
//   BF.toast.promise(promise, { loading, success, error })
//   BF.toast.dismiss(id?)        // omit id → dismiss all
//   BF.toast.setPosition(pos)    // 'bottom-right' (default), 'top-center', …
//
// The server/htmx integration also accepts common alert aliases:
// `tone/status/variant/level` for `type`, and `text/message/value` for copy.

import { $$, uid } from './util';

export type Position =
  | 'top-left' | 'top-right' | 'top-center'
  | 'bottom-left' | 'bottom-right' | 'bottom-center';

export type ToastType = 'default' | 'success' | 'error' | 'warning' | 'info' | 'loading';

interface ActionSpec { label: string; onClick?: () => void }

export interface ToastOptions {
  title?: string;
  description?: string;
  type?: ToastType;
  duration?: number;          // ms; 0 = sticky
  action?: ActionSpec;
  cancel?: ActionSpec;
  id?: string;
  position?: Position;
}

export interface ToastInput extends Omit<ToastOptions, 'type' | 'duration' | 'position'> {
  type?: ToastType | string;
  duration?: number | string;
  position?: Position | string;
  tone?: ToastType | string;
  status?: ToastType | string;
  variant?: ToastType | string;
  level?: ToastType | string;
  text?: string;
  message?: string;
  value?: string;
}

export interface ToastInstance {
  id: string;
  dismiss(): void;
  update(opts: Partial<ToastInput>): void;
}

const ICONS: Record<ToastType, string> = {
  default: '<i class="fa-duotone fa-bell"></i>',
  success: '<i class="fa-duotone fa-circle-check"></i>',
  error:   '<i class="fa-duotone fa-circle-exclamation"></i>',
  warning: '<i class="fa-duotone fa-triangle-exclamation"></i>',
  info:    '<i class="fa-duotone fa-circle-info"></i>',
  loading: '',  // rendered as a spinning ring via .bf-toast__icon--spin
};

const DEFAULT_DURATION: Record<ToastType, number> = {
  default: 4000,
  success: 4000,
  info:    4000,
  warning: 6000,
  error:   8000,
  loading: 0,
};

let host: HTMLElement | null = null;
let position: Position = 'bottom-right';
const active = new Map<string, ToastEntry>();
let globalsBound = false;

interface ToastEntry {
  id: string;
  el: HTMLElement;
  opts: ToastOptions;
  timeout?: number;
  startedAt?: number;
  remaining?: number;
}

function ensureHost(): HTMLElement {
  if (host && host.dataset.bfToastPos === position) return host;
  if (host) host.remove();
  host = document.createElement('ol');
  host.className = 'bf-toaster';
  host.dataset.bfToastPos = position;
  // Hover pauses every active dismissal timer AND splays the collapsed
  // stack into a readable list — Sonner's twin-purpose hover behaviour.
  host.addEventListener('mouseenter', () => { pauseAll(); expand(); });
  host.addEventListener('mouseleave', () => { resumeAll(); collapse(); });
  host.addEventListener('focusin',    () => { pauseAll(); expand(); });
  host.addEventListener('focusout',   () => { resumeAll(); collapse(); });
  host.dataset.expanded = 'false';
  document.body.appendChild(host);
  return host;
}

function expand(): void  { if (host) host.dataset.expanded = 'true'; }
function collapse(): void { if (host) host.dataset.expanded = 'false'; }

/* Walk the active list and write the stack-depth attributes the CSS reads.
 * The "front" toast is the most-recently-shown one. For bottom-* positions
 * that's the LAST element in DOM order; for top-* positions it's the FIRST.
 * Also measures each toast's rendered height into a CSS var so the
 * expanded layout can spread them proportionally. */
function restack(): void {
  if (!host) return;
  const els = Array.from(host.querySelectorAll<HTMLElement>('.bf-toast'));
  const ordered = position.startsWith('bottom') ? els.slice().reverse() : els;
  ordered.forEach((el, i) => {
    el.dataset.bfStackIndex = String(i);
    el.style.setProperty('--_idx', String(i));
    el.style.setProperty('--bf-toast-h', `${el.offsetHeight}px`);
  });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

function render(entry: ToastEntry): void {
  const o = entry.opts;
  const type = o.type ?? 'default';
  const el = entry.el;
  el.className = `bf-toast bf-toast--${type}`;
  el.role = type === 'error' || type === 'warning' ? 'alert' : 'status';
  el.dataset.bfId = entry.id;

  const iconHtml = type === 'loading'
    ? `<span class="bf-toast__icon bf-toast__icon--spin" aria-hidden="true"></span>`
    : `<span class="bf-toast__icon" aria-hidden="true">${ICONS[type]}</span>`;

  const body = [];
  if (o.title)       body.push(`<div class="bf-toast__title">${escapeHtml(o.title)}</div>`);
  if (o.description) body.push(`<div class="bf-toast__desc">${escapeHtml(o.description)}</div>`);

  const actions = [];
  if (o.action) actions.push(`<button type="button" class="bf-toast__action" data-bf-toast-action>${escapeHtml(o.action.label)}</button>`);
  if (o.cancel) actions.push(`<button type="button" class="bf-toast__cancel" data-bf-toast-cancel>${escapeHtml(o.cancel.label)}</button>`);
  const actionsHtml = actions.length ? `<div class="bf-toast__actions">${actions.join('')}</div>` : '';

  el.innerHTML = `
    ${iconHtml}
    <div class="bf-toast__body">
      ${body.join('')}
      ${actionsHtml}
    </div>
    <button type="button" class="bf-toast__close" aria-label="Close" data-bf-toast-close>
      <i class="fa-solid fa-xmark"></i>
    </button>
  `;

  el.querySelector('[data-bf-toast-action]')?.addEventListener('click', () => {
    o.action?.onClick?.();
    dismiss(entry.id);
  });
  el.querySelector('[data-bf-toast-cancel]')?.addEventListener('click', () => {
    o.cancel?.onClick?.();
    dismiss(entry.id);
  });
  el.querySelector('[data-bf-toast-close]')?.addEventListener('click', () => dismiss(entry.id));

  bindSwipe(el, entry.id);
}

function bindSwipe(el: HTMLElement, id: string): void {
  // Right-swipe dismissal (mouse + touch). Sonner's directionality is fancier;
  // we keep it simple and only honor a horizontal drag past 60px.
  let startX = 0;
  let dragging = false;

  const onDown = (e: PointerEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    dragging = true;
    startX = e.clientX;
    el.dataset.bfSwipe = 'true';
    el.setPointerCapture(e.pointerId);
  };
  const onMove = (e: PointerEvent) => {
    if (!dragging) return;
    const dx = Math.max(0, e.clientX - startX);
    el.style.setProperty('--_swipe', `${dx}px`);
    el.style.opacity = `${Math.max(0, 1 - dx / 200)}`;
  };
  const onUp = (e: PointerEvent) => {
    if (!dragging) return;
    dragging = false;
    const dx = e.clientX - startX;
    el.removeAttribute('data-bf-swipe');
    el.style.removeProperty('--_swipe');
    el.style.removeProperty('opacity');
    if (dx > 60) dismiss(id);
  };
  el.addEventListener('pointerdown', onDown);
  el.addEventListener('pointermove', onMove);
  el.addEventListener('pointerup', onUp);
  el.addEventListener('pointercancel', onUp);
}

function startTimer(entry: ToastEntry): void {
  const duration = entry.remaining ?? entry.opts.duration ?? DEFAULT_DURATION[entry.opts.type ?? 'default'];
  if (!duration) return;
  entry.startedAt = performance.now();
  entry.remaining = duration;
  entry.timeout = window.setTimeout(() => dismiss(entry.id), duration);
}

function pauseAll(): void {
  if (host) host.dataset.expanded = 'true';
  for (const entry of active.values()) {
    if (!entry.timeout) continue;
    window.clearTimeout(entry.timeout);
    delete entry.timeout;
    if (entry.startedAt != null && entry.remaining != null) {
      entry.remaining = Math.max(0, entry.remaining - (performance.now() - entry.startedAt));
    }
  }
}
function resumeAll(): void {
  if (host) host.dataset.expanded = 'false';
  for (const entry of active.values()) startTimer(entry);
}

export function init(root: ParentNode = document): void {
  bindGlobals();
  for (const el of $$<HTMLElement>('[data-bf-toast]', root)) {
    const opts = toastOptionsFromElement(el);
    if (opts) show(opts);
    el.remove();
  }
}

function bindGlobals(): void {
  if (globalsBound) return;
  globalsBound = true;
  document.addEventListener('bf:toast', (event) => {
    const opts = toastOptionsFromDetail((event as CustomEvent<unknown>).detail);
    if (opts) show(opts);
  });
}

function toastOptionsFromElement(el: HTMLElement): ToastOptions | null {
  const opts = normalizeToastInput({
    title: el.dataset.bfToast || el.dataset.title || el.dataset.message || el.getAttribute('title') || '',
    description: el.dataset.description || el.dataset.text || el.textContent?.trim() || '',
    type: el.dataset.type || '',
    tone: el.dataset.tone || '',
    status: el.dataset.status || '',
    variant: el.dataset.variant || '',
    level: el.dataset.level || '',
    id: el.dataset.id || '',
    position: el.dataset.position || '',
    duration: el.dataset.duration || '',
  });
  return opts.title || opts.description ? opts : null;
}

function toastOptionsFromDetail(detail: unknown): ToastOptions | null {
  if (typeof detail === 'string') {
    return detail.trim() ? { title: detail.trim() } : null;
  }
  if (!detail || typeof detail !== 'object') return null;
  const opts = normalizeToastInput(detail as ToastInput);
  return opts.title || opts.description ? opts : null;
}

function stringValue(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

function actionSpec(v: unknown): ActionSpec | undefined {
  if (!v || typeof v !== 'object') return undefined;
  const input = v as Record<string, unknown>;
  const label = stringValue(input.label);
  if (!label) return undefined;
  const spec: ActionSpec = { label };
  if (typeof input.onClick === 'function') spec.onClick = input.onClick as () => void;
  return spec;
}

function normalizeToastInput(input: ToastInput): ToastOptions {
  const source = input as Record<string, unknown>;
  const title = stringValue(source.title) || stringValue(source.message) || stringValue(source.value);
  const description = stringValue(source.description) || stringValue(source.text);
  const type = coerceToastType(
    stringValue(source.type) ||
    stringValue(source.tone) ||
    stringValue(source.status) ||
    stringValue(source.variant) ||
    stringValue(source.level)
  );
  const id = stringValue(source.id);
  const positionValue = coercePosition(stringValue(source.position));
  const duration = coerceDuration(source.duration);
  const action = actionSpec(source.action);
  const cancel = actionSpec(source.cancel);
  const opts: ToastOptions = {};
  if (title) opts.title = title;
  if (description) opts.description = description;
  if (type) opts.type = type;
  if (duration !== undefined) opts.duration = duration;
  if (action) opts.action = action;
  if (cancel) opts.cancel = cancel;
  if (id) opts.id = id;
  if (positionValue) opts.position = positionValue;
  return opts;
}

function coerceDuration(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return Math.max(0, v);
  if (typeof v !== 'string' || v.trim() === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, n) : undefined;
}

function coerceToastType(v: string): ToastType | undefined {
  switch (v) {
    case 'default':
    case 'success':
    case 'error':
    case 'warning':
    case 'info':
    case 'loading':
      return v;
    case 'danger':
    case 'destructive':
      return 'error';
    case 'warn':
      return 'warning';
    case 'notice':
      return 'info';
    case 'neutral':
      return 'default';
    default:
      return undefined;
  }
}

function coercePosition(v: string): Position | undefined {
  switch (v) {
    case 'top-left':
    case 'top-right':
    case 'top-center':
    case 'bottom-left':
    case 'bottom-right':
    case 'bottom-center':
      return v;
    default:
      return undefined;
  }
}

export function show(input: ToastInput): ToastInstance {
  const opts = normalizeToastInput(input);
  const id = opts.id ?? uid('bf-toast');
  if (opts.position && opts.position !== position) setPosition(opts.position);
  const h = ensureHost();

  // Update existing instance with the same id (for promise() upgrades).
  const existing = active.get(id);
  if (existing) {
    existing.opts = normalizeToastInput({ ...existing.opts, ...input });
    render(existing);
    if (existing.timeout) window.clearTimeout(existing.timeout);
    delete existing.startedAt;
    delete existing.remaining;
    startTimer(existing);
    return makeHandle(id);
  }

  const el = document.createElement('li');
  el.dataset.mounted = 'false';
  const entry: ToastEntry = { id, el, opts };
  active.set(id, entry);

  // Top-pos toaster wants newest at top, bottom-pos wants newest at bottom.
  if (position.startsWith('bottom')) h.appendChild(el);
  else h.insertBefore(el, h.firstChild);

  render(entry);
  // Trigger entrance on the next frame so the initial "off-screen" state is
  // committed first. Restack inside the same frame so the new toast lands at
  // index 0 (front) and the previous front slides into index 1.
  requestAnimationFrame(() => {
    el.dataset.mounted = 'true';
    restack();
  });
  startTimer(entry);

  return makeHandle(id);
}

function makeHandle(id: string): ToastInstance {
  return {
    id,
    dismiss: () => dismiss(id),
    update: (opts) => {
      const entry = active.get(id);
      if (!entry) return;
      entry.opts = normalizeToastInput({ ...entry.opts, ...opts });
      render(entry);
      if (entry.timeout) window.clearTimeout(entry.timeout);
      delete entry.startedAt;
      delete entry.remaining;
      startTimer(entry);
    },
  };
}

export function dismiss(id?: string): void {
  if (!id) {
    for (const entry of Array.from(active.values())) removeEntry(entry);
    return;
  }
  const entry = active.get(id);
  if (entry) removeEntry(entry);
}

function removeEntry(entry: ToastEntry): void {
  if (entry.timeout) window.clearTimeout(entry.timeout);
  entry.el.dataset.leave = 'true';
  entry.el.dataset.mounted = 'false';
  entry.el.addEventListener('transitionend', () => {
    entry.el.remove();
    active.delete(entry.id);
    restack();
  }, { once: true });
  // Fallback in case transitionend doesn't fire (display: none ancestors etc.)
  window.setTimeout(() => {
    if (entry.el.isConnected) {
      entry.el.remove();
      active.delete(entry.id);
      restack();
    }
  }, 600);
}

export function setPosition(pos: Position): void {
  position = pos;
  ensureHost();
}

// Sugar.
export const success = (title: string, opts: Partial<ToastInput> = {}) => show({ title, type: 'success', ...opts });
export const error   = (title: string, opts: Partial<ToastInput> = {}) => show({ title, type: 'error',   ...opts });
export const warning = (title: string, opts: Partial<ToastInput> = {}) => show({ title, type: 'warning', ...opts });
export const info    = (title: string, opts: Partial<ToastInput> = {}) => show({ title, type: 'info',    ...opts });
export const loading = (title: string, opts: Partial<ToastInput> = {}) => show({ title, type: 'loading', duration: 0, ...opts });

interface PromiseSpec<T> {
  loading: string;
  success: string | ((value: T) => string);
  error:   string | ((err: unknown) => string);
}

export function promise<T>(p: Promise<T>, spec: PromiseSpec<T>): ToastInstance {
  const handle = loading(spec.loading);
  p.then((val) => {
    const msg = typeof spec.success === 'function' ? spec.success(val) : spec.success;
    handle.update({ type: 'success', title: msg, duration: DEFAULT_DURATION.success });
  }, (err) => {
    const msg = typeof spec.error === 'function' ? spec.error(err) : spec.error;
    handle.update({ type: 'error', title: msg, duration: DEFAULT_DURATION.error });
  });
  return handle;
}
